import { Link, MenuItem, Select, Stack, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTranslation } from 'react-i18next';

type BookingGearScopeBarProps = {
  /** -1 edits every session. Zero or more edits that date only. */
  focusIndex: number;
  varies: boolean;
  sessionLabels: string[];
  onFocusIndexChange: (index: number) => void;
};

/**
 * Series gear scope. Matches the mobile booking stepper: one control keeps
 * the same gear on every date, and "Change one" picks a single session.
 */
export default function BookingGearScopeBar({
  focusIndex,
  varies,
  sessionLabels,
  onFocusIndexChange,
}: BookingGearScopeBarProps) {
  const { t } = useTranslation();
  const editingOne = focusIndex >= 0;
  const selectedIndex = Math.min(Math.max(focusIndex, 0), Math.max(sessionLabels.length - 1, 0));

  const rowSx = { px: { xs: 1, sm: 2 }, py: 1.5 };
  const linkSx = {
    p: 0,
    m: 0,
    minWidth: 0,
    border: 'none',
    background: 'none',
    color: (theme: Theme) => `${theme.palette.primary.main} !important`,
    fontFamily: 'inherit',
    fontSize: '0.875rem',
    fontWeight: 600,
    lineHeight: 1.43,
    cursor: 'pointer',
    textUnderlineOffset: 3,
  };

  if (!editingOne) {
    return (
      <Stack direction="row" alignItems="center" spacing={2} sx={rowSx}>
        <Typography
          variant="body2"
          sx={{ flex: 1, minWidth: 0, fontWeight: 600, color: 'text.secondary', lineHeight: 1.43 }}
          noWrap
        >
          {varies
            ? t('booking.gearVariesBySession', 'Gear differs by session')
            : t('booking.gearAppliesToEverySession', 'Same for every session')}
        </Typography>
        <Link
          component="button"
          type="button"
          variant="body2"
          underline="hover"
          onClick={() => onFocusIndexChange(0)}
          sx={{ ...linkSx, flexShrink: 0, textAlign: 'right' }}
        >
          {t('booking.gearEditOneSession', 'Change one')}
        </Link>
      </Stack>
    );
  }

  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={rowSx}>
      <Link
        component="button"
        type="button"
        variant="body2"
        underline="hover"
        onClick={() => onFocusIndexChange(-1)}
        sx={{ ...linkSx, display: 'inline-flex', alignItems: 'center', gap: 0.5, color: 'text.primary' }}
      >
        <ArrowBackIcon sx={{ fontSize: 16 }} />
        {t('booking.gearAllSessions', 'All sessions')}
      </Link>
      <Select
        size="small"
        value={selectedIndex}
        onChange={(event) => onFocusIndexChange(Number(event.target.value))}
        sx={{
          ml: 'auto',
          minWidth: 0,
          fontWeight: 700,
          '& .MuiSelect-select': { py: 0.75 },
        }}
      >
        {sessionLabels.map((label, index) => (
          <MenuItem key={`${label}-${index}`} value={index}>
            {label}
          </MenuItem>
        ))}
      </Select>
    </Stack>
  );
}
